import { useState, useEffect } from 'react';
import { load, CashfreeInstance, PaymentResult, CheckoutOptions, CashfreeConfig } from '@cashfreepayments/cashfree-js';
import { CASHFREE_MODE, REDIRECT_TARGET, CASHFREE_THEME } from '../config/cashfreeConfig';

interface UseCashfreeOptions {
  mode?: 'sandbox' | 'production';
  debug?: boolean;
}

interface UseCashfreeReturn {
  cashfree: CashfreeInstance | null;
  isLoading: boolean;
  error: Error | null;
  initiatePayment: (paymentSessionId: string, checkoutOptions?: Partial<CheckoutOptions>) => Promise<PaymentResult>;
}

const useCashfree = (hookOptions: UseCashfreeOptions = {}): UseCashfreeReturn => {
  const { mode = CASHFREE_MODE, debug = false } = hookOptions;

  const [cashfree, setCashfree] = useState<CashfreeInstance | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Helper function for logging when debug is enabled
  const log = (message: string, data?: unknown): void => {
    if (debug) {
      if (data) {
        // eslint-disable-next-line no-console
        console.log(`[Cashfree] ${message}`, data);
      } else {
        // eslint-disable-next-line no-console
        console.log(`[Cashfree] ${message}`);
      }
    }
  };

  // Initialize Cashfree SDK on component mount
  useEffect(() => {
    const initCashfree = async (): Promise<void> => {
      try {
        log(`Initializing Cashfree SDK in ${mode} mode...`);

        const config: CashfreeConfig = { mode };
        const cashfreeInstance = await load(config);

        log('Cashfree SDK initialized successfully');
        setCashfree(cashfreeInstance);
        setIsLoading(false);
      } catch (err) {
        log('Failed to load Cashfree SDK', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setIsLoading(false);
      }
    };

    initCashfree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, debug]);

  const initiatePayment = async (
    paymentSessionId: string,
    checkoutOptions: Partial<CheckoutOptions> = {},
  ): Promise<PaymentResult> => {
    if (!cashfree) {
      throw new Error('Cashfree SDK not initialized');
    }

    if (!paymentSessionId) {
      throw new Error('Payment session ID is required');
    }

    log('Initiating payment with session ID', paymentSessionId);

    const finalOptions: CheckoutOptions = {
      paymentSessionId,
      redirectTarget: REDIRECT_TARGET,
      theme: CASHFREE_THEME,
      ...checkoutOptions,
    };

    try {
      log('Opening Cashfree checkout...');
      const result = await cashfree.checkout(finalOptions);

      log('Payment result', result);
      return result;
    } catch (err) {
      log('Checkout error', err);
      throw err instanceof Error ? err : new Error(String(err));
    }
  };

  return {
    cashfree,
    isLoading,
    error,
    initiatePayment,
  };
};

export default useCashfree;
